import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);
  const navLinks = [{
    name: 'About Us',
    path: '/about'
  }, {
    name: 'Booking/Pricing',
    path: '/pricing'
  }, {
    name: 'Services',
    path: '/services'
  }, {
    name: 'Who We Serve',
    path: '/who-we-serve'
  }, {
    name: 'Contact Us',
    path: '/contact'
  }];
  const isActive = (path: string) => location.pathname === path;
  return <header role="banner" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#060C1A]/95 backdrop-blur-md shadow-lg py-3 border-b border-white/10' : 'bg-transparent py-5'}`} data-id="element-154">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-155">
        <div className="flex justify-between items-center" data-id="element-156">
          {/* Logo */}
          <Link to="/" className="flex items-center group" aria-label="Ambient Fingerprinting - Home" data-id="element-157">
            <img src="https://cdn.magicpatterns.com/uploads/45tD6GiCQLAvjHvfZnL4vs/cropped-Media__1_-removebg-preview.png" alt="Ambient Fingerprinting" className="h-10 md:h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105" data-id="element-158" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6" aria-label="Main navigation" data-id="element-159">
            {navLinks.map(link => <Link key={link.name} to={link.path} aria-current={isActive(link.path) ? 'page' : undefined} className={`text-sm font-medium transition-colors ${isActive(link.path) ? 'text-[#4AA6CF]' : 'text-white/90 hover:text-[#4AA6CF]'}`} data-id="element-160">
                {link.name}
              </Link>)}
            <Link to="/booking" className="bg-gradient-to-r from-[#4AA6CF] to-[#3b85a6] text-white hover:shadow-[0_0_15px_rgba(74,166,207,0.5)] px-6 py-2.5 rounded-full text-sm font-semibold transition-all" data-id="element-161">
              Book Appointment
            </Link>
          </nav>

          {/* Mobile Menu Toggle */}
          <button className="lg:hidden text-white p-2 hover:text-[#4AA6CF] transition-colors" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open navigation menu" aria-expanded={isMobileMenuOpen} aria-controls="mobile-nav" data-id="element-162">
            <MenuIcon className="h-6 w-6" aria-hidden="true" data-id="element-163" />
          </button>
        </div>
      </div>

      {/* Mobile Nav Panel */}
      <AnimatePresence data-id="element-164">
        {isMobileMenuOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} aria-hidden="true" data-id="element-165" />
            <motion.div id="mobile-nav" role="dialog" aria-modal="true" aria-label="Navigation menu" initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed top-0 right-0 bottom-0 w-3/4 max-w-sm bg-[#060C1A] border-l border-white/10 z-50 shadow-2xl flex flex-col" data-id="element-166">
              <div className="p-5 flex justify-between items-center border-b border-white/10" data-id="element-167">
                <img src="https://cdn.magicpatterns.com/uploads/45tD6GiCQLAvjHvfZnL4vs/cropped-Media__1_-removebg-preview.png" alt="Ambient Fingerprinting" className="h-8 w-auto object-contain" data-id="element-168" />
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-gray-400 hover:text-white transition-colors" aria-label="Close navigation menu" autoFocus data-id="element-169">
                  <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-170" />
                </button>
              </div>
              <nav className="flex flex-col p-5 gap-4" aria-label="Mobile navigation" data-id="element-171">
                {navLinks.map(link => <Link key={link.name} to={link.path} aria-current={isActive(link.path) ? 'page' : undefined} className={`font-medium text-lg py-3 border-b border-white/5 transition-colors ${isActive(link.path) ? 'text-[#4AA6CF]' : 'text-white/90 hover:text-[#4AA6CF]'}`} onClick={() => setIsMobileMenuOpen(false)} data-id="element-172">
                    {link.name}
                  </Link>)}
                <Link to="/booking" className="mt-6 bg-gradient-to-r from-[#4AA6CF] to-[#3b85a6] text-white text-center py-3 rounded-full font-semibold shadow-lg" onClick={() => setIsMobileMenuOpen(false)} data-id="element-173">
                  Book Appointment
                </Link>
              </nav>
            </motion.div>
          </>}
      </AnimatePresence>
    </header>;
}